import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  DollarSign, Briefcase, Image, MessageSquare, MapPin,
  HelpCircle, Phone, Home, LogOut, Settings, Inbox
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const sections = [
    {
      title: 'Contact Submissions',
      description: 'View messages sent through the website contact form',
      icon: Inbox,
      link: '/admin/submissions',
      color: 'from-teal-500 to-teal-600'
    },
    {
      title: 'Pricing Management',
      description: 'Manage window cleaning and gutter cleaning prices',
      icon: DollarSign,
      link: '/admin/pricing',
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Services',
      description: 'Edit service descriptions and features',
      icon: Briefcase,
      link: '/admin/services',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      title: 'Gallery',
      description: 'Manage gallery images and descriptions',
      icon: Image,
      link: '/admin/gallery',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      title: 'Testimonials',
      description: 'Add and edit customer testimonials',
      icon: MessageSquare,
      link: '/admin/testimonials',
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Coverage Areas',
      description: 'Update service coverage areas list',
      icon: MapPin,
      link: '/admin/coverage-areas',
      color: 'from-orange-500 to-orange-600'
    },
    {
      title: 'FAQs',
      description: 'Manage frequently asked questions',
      icon: HelpCircle,
      link: '/admin/faqs',
      color: 'from-pink-500 to-pink-600'
    },
    {
      title: 'Contact Info',
      description: 'Update phone, email, and business hours',
      icon: Phone,
      link: '/admin/contact-info',
      color: 'from-red-500 to-red-600'
    },
    {
      title: 'Home Page Content',
      description: 'Edit hero text and key selling points',
      icon: Home,
      link: '/admin/home-content',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-cyan-100">Tom The Window Cleaner CMS</p>
          </div>
          <div className="flex gap-3">
            <Link to="/">
              <Button variant="outline" className="text-white border-white hover:bg-white/10">
                View Website
              </Button>
            </Link>
            <Link to="/admin/settings">
              <Button variant="outline" className="text-white border-white hover:bg-white/10">
                <Settings className="mr-2 w-4 h-4" />
                Settings
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white hover:bg-white/10">
              <LogOut className="mr-2 w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link key={index} to={section.link}>
                <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-cyan-300 h-full">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{section.description}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
